import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Checkbox from 'antd/es/checkbox';
import Col from 'antd/es/col';
import { useForm } from 'antd/es/form/Form';
import Input from 'antd/es/input';
import Row from 'antd/es/row';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GMAPS_API_KEY } from '../../../api';
import { getLongAddress } from '../../../features/helpers/get-address';
import useLocale from '../../../features/providers/locale-provider';
import { useTranslations } from '../../../features/providers/translations-provider';
import { Status, Wrapper } from '../../../features/wrappers/gmaps-wrapper';
import Collapse from '../Collapse';
import SelectCountry from '../country-select';
import FormItem from '../form-item';
import FormLabel from '../form-label';
import SelectProvince from '../province-select';
import GoogleAutocomplete from './GoogleAutocomplete';
import { AddressAutocompleteContainer } from './styles';
const AddressAutocompleteInput = ({ form, showManualAddress, required, provinceRequired, disabled, onAddressChange, cityOnly, label, icon, size, completeAddressFieldName = 'complete', }) => {
    const { t } = useTranslations();
    const { selectedLocale } = useLocale();
    const [isManual, setManual] = useState(false);
    const [isCanada, setCanada] = useState(false);
    const getCompleteFromForm = useCallback(() => {
        const values = form.getFieldsValue();
        const complete = getLongAddress(values.address, {
            city: values.city,
            address: values.address,
            country: values.country,
            postal_code: values.postal_code,
            province: values.province,
        });
        const completeValue = !complete || complete === 'undefined' ? '' : complete;
        form.setFieldValue(completeAddressFieldName, completeValue);
    }, [completeAddressFieldName, form]);
    useEffect(() => {
        getCompleteFromForm();
    }, [getCompleteFromForm]);
    useEffect(() => {
        const canadaValue = form.getFieldValue('isCanada');
        setCanada(canadaValue);
    }, [form]);
    useEffect(() => {
        if (isCanada && isManual) {
            form.setFieldValue('country', 'Canada');
        }
    }, [isCanada, isManual, form]);
    const render = useCallback((status) => {
        if (status === Status.FAILURE || status === Status.LOADING) {
            return null;
        }
        return (_jsx(GoogleAutocomplete, { form: form, setCanada: setCanada, disabled: isManual || disabled, onAddressChange: () => {
                getCompleteFromForm();
                onAddressChange === null || onAddressChange === void 0 ? void 0 : onAddressChange();
            }, cityOnly: cityOnly, completeAddressFieldName: completeAddressFieldName }));
    }, [
        isManual,
        disabled,
        form,
        getCompleteFromForm,
        onAddressChange,
        cityOnly,
        completeAddressFieldName,
    ]);
    const handleChange = useCallback(() => {
        getCompleteFromForm();
        onAddressChange === null || onAddressChange === void 0 ? void 0 : onAddressChange();
    }, [getCompleteFromForm, onAddressChange]);
    return (_jsx(AddressAutocompleteContainer, { style: { marginBottom: 16 }, children: _jsx(FormItem, { name: completeAddressFieldName, label: _jsx(FormLabel, { Icon: icon, label: label !== null && label !== void 0 ? label : t('profile|profileForm.company.labels.address'), size: size }), style: { margin: 0 }, rules: useMemo(() => {
                if (!required && !provinceRequired)
                    return [];
                return [
                    {
                        required: true,
                        message: provinceRequired && !required
                            ? t('profile|profileForm.address.validation.province')
                            : t('profile|profileForm.company.validation.address'),
                    },
                ];
            }, [required, provinceRequired, t]), extra: _jsx(Collapse, { noPadding: true, ExpandIcon: () => _jsx(_Fragment, {}), ghost: true, items: [
                    {
                        id: 'autocomplete',
                        title: showManualAddress ? (_jsx(Checkbox, { style: { marginTop: 16 }, onChange: (e) => setManual(e.target.checked), disabled: disabled, children: t('profile|profileForm.address.extra.addressManually') })) : (_jsx(_Fragment, {})),
                        isActive: isManual,
                        children: (_jsx(_Fragment, { children: _jsxs(Row, { gutter: [32, 16], style: {
                                    padding: '16px 0',
                                }, children: [_jsx(Col, { span: 24, children: _jsx(FormItem, { name: "address", layout: "vertical", label: _jsx(FormLabel, { label: t('profile|profileForm.address.labels.address') }), children: _jsx(Input, { size: "large", onChange: handleChange, disabled: disabled }) }) }), _jsx(Col, { span: 8, children: _jsx(SelectCountry, { setCanada: setCanada, form: form, onChange: handleChange }) }), _jsx(Col, { span: isCanada ? 0 : 8, children: _jsx(FormItem, { hidden: isCanada, name: "country", layout: "vertical", label: _jsx(FormLabel, { label: t('profile|profileForm.address.labels.otherCountry') }), rules: [
                                                {
                                                    message: t('profile|profileForm.address.validation.country'),
                                                },
                                            ], children: _jsx(Input, { size: "large", onChange: handleChange, disabled: disabled, placeholder: t('profile|profileForm.address.placeholder.otherCountry') }) }) }), _jsx(Col, { span: 8, children: _jsx(SelectProvince, { isRequired: provinceRequired, onChange: handleChange, isCanada: isCanada, disabled: disabled }) }), _jsx(Col, { span: 8, children: _jsx(FormItem, { name: "city", layout: "vertical", label: _jsx(FormLabel, { label: t('profile|profileForm.address.labels.city') }), children: _jsx(Input, { size: "large", onChange: handleChange, disabled: disabled }) }) }), _jsx(Col, { span: 8, children: _jsx(FormItem, { name: "postal_code", layout: "vertical", label: _jsx(FormLabel, { label: t(`profile|profileForm.address.labels.${isCanada ? 'postalCode' : 'zipCode'}`) }), children: _jsx(Input, { size: "large", onChange: handleChange, disabled: disabled }) }) }), _jsx(Col, { span: 8, children: _jsx(FormItem, { name: "lat", layout: "vertical", hidden: true, children: _jsx(Input, { size: "large", onChange: handleChange, disabled: disabled }) }) }), _jsx(Col, { span: 8, children: _jsx(FormItem, { name: "lng", layout: "vertical", hidden: true, children: _jsx(Input, { size: "large", disabled: disabled }) }) })] }) })),
                    },
                ] }), children: _jsx(Wrapper, { apiKey: GMAPS_API_KEY, language: `${selectedLocale || 'en'}-ca`, region: "CA", id: "GOOGLE_MAPS", render: render, libraries: ['maps', 'places'], library: "places" }) }) }));
};
AddressAutocompleteInput.displayName = 'AddressAutocompleteInput';
const AutocompleteDemo = () => {
    const [form] = useForm();
    return _jsx(AddressAutocompleteInput, { form: form, showManualAddress: true });
};
AddressAutocompleteInput.Demo =
    AutocompleteDemo;
export default AddressAutocompleteInput;
